import React from "react";
import Select from "react-select";
import BottomSheet from "../../../components/ui/BottomSheet";
import SheetHeader from "../../../components/ui/SheetHeader";
import Button from "../../../components/ui/Button";
import PaymentsInput from "./PaymentsInput";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { ICity, ISaleForm } from "../../../classes/interfaces";
import { Body, SelectLabel, SelectHint, Footer } from "./NewAddressModal.styles";

/**
 * Campos do formulário de endereço. É um estado próprio, separado do endereço
 * já selecionado para o pedido: editar/cadastrar não mexe na seleção nem na
 * taxa até o backend confirmar a taxa do endereço salvo.
 */
export type AddressFormValues = Pick<
  ISaleForm,
  | "zipCode"
  | "address"
  | "number"
  | "district"
  | "districtId"
  | "cityId"
  | "city"
  | "complement"
  | "reference"
>;

interface NewAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  formValues: AddressFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<AddressFormValues>>;
  cities?: ICity[];
  cityOptions: { value: string; label: string }[];
  districts: { value: number; label: string; tax: number }[];
  onCityChange: (cityId: number, label: string) => void;
  onDistrictChange: (districtId: number, label: string, tax: number) => void;
  onSubmit: () => void;
  editing?: boolean;
}

export default function NewAddressModal({
  isOpen,
  onClose,
  formValues,
  setFormValues,
  cities,
  cityOptions,
  districts,
  onCityChange,
  onDistrictChange,
  onSubmit,
  editing = false,
}: NewAddressModalProps) {
  const desktop = useIsDesktopViewport();

  // Select controlado: mostra o bairro do formulário só se ele estiver entre os
  // bairros da loja carregados para a cidade. Um districtId velho (loja
  // recriada, bairro apagado, bairro de outra loja) não aparece selecionado.
  const selectedDistrict =
    districts.find((district) => district.value === formValues.districtId) ?? null;

  return (
    <BottomSheet open={isOpen} onClose={onClose} centered={desktop} maxHeight="90%">
      <SheetHeader
        title={editing ? "Editar Endereço" : "Novo Endereço"}
        sub={
          editing
            ? "Confirme o bairro para atualizar a taxa de entrega"
            : "Cadastre um novo endereço para entrega"
        }
        onClose={onClose}
      />
      <Body>
        <PaymentsInput
          label="CEP"
          name="zipCode"
          value={formValues.zipCode}
          onChange={setFormValues}
          maxLenght={8}
          numberOnly={true}
        />

        {cities ? (
          <div style={{ marginBottom: 13 }}>
            <SelectLabel>Cidade:</SelectLabel>
            <Select
              className="basic-single"
              classNamePrefix="select"
              defaultValue={{ value: formValues.cityId.toString(), label: formValues.city }}
              isDisabled={false}
              isLoading={false}
              isClearable={false}
              isRtl={false}
              isSearchable={true}
              options={cityOptions}
              onChange={(option) => {
                if (option !== null) {
                  onCityChange(Number.parseInt(option.value), option.label);
                }
              }}
            />
          </div>
        ) : (
          <PaymentsInput
            label="Cidade"
            name="city"
            value={formValues.city}
            onChange={setFormValues}
          />
        )}

        <PaymentsInput
          label="Endereço"
          name="address"
          value={formValues.address}
          onChange={setFormValues}
          maxLenght={100}
        />

        <PaymentsInput
          label="Número"
          name="number"
          value={formValues.number}
          onChange={setFormValues}
          maxLenght={10}
        />

        {districts.length > 0 ? (
          <div style={{ marginBottom: 13 }}>
            <SelectLabel>Bairro:</SelectLabel>
            <Select
              className="basic-single"
              classNamePrefix="select"
              inputId="district-select"
              aria-label="Bairro"
              placeholder="Selecione o bairro"
              noOptionsMessage={() => "Nenhum bairro encontrado"}
              value={selectedDistrict}
              isDisabled={false}
              isLoading={false}
              isClearable={false}
              isRtl={false}
              isSearchable={true}
              options={districts}
              onChange={(option) => {
                onDistrictChange(
                  Number.parseInt(option!.value.toString()),
                  option!.label,
                  option!.tax,
                );
              }}
            />
            {selectedDistrict === null && formValues.district ? (
              <SelectHint>
                Bairro salvo: {formValues.district}. Selecione o bairro na lista para
                confirmar a taxa de entrega.
              </SelectHint>
            ) : null}
          </div>
        ) : (
          <PaymentsInput
            label="Bairro"
            name="district"
            value={formValues.district}
            onChange={setFormValues}
            maxLenght={80}
          />
        )}

        <PaymentsInput
          label="Complemento"
          name="complement"
          value={formValues.complement}
          onChange={setFormValues}
          maxLenght={100}
        />

        <PaymentsInput
          label="Referência"
          name="reference"
          value={formValues.reference}
          onChange={setFormValues}
          maxLenght={100}
        />
      </Body>
      <Footer>
        <Button variant="ghost" full onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" full onClick={onSubmit}>
          Salvar
        </Button>
      </Footer>
    </BottomSheet>
  );
}
